import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UserGroupMark from './UserGroupMark';
import { USER_TYPE_SHORT_LABELS } from './userGroupConstants';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// TODO(mobile): below ~480px the chip should collapse to mark+chevron only
// with the name/role accessible via the menu. Follow-up ticket.
const NAME_MAX_WIDTH = 180;
const ROLE_SHORT_MAX = 12;

// Always-visible pill in the app bar that surfaces the user's active User
// Group + role. Adjoined to the profile avatar (the avatar overlaps the
// chip's right edge — see UserGroupChipCluster for the positioning).
const UserGroupChip = React.forwardRef(function UserGroupChip(props, ref) {
  const {
    userGroup,
    roleFullName,
    isMultiGroup,
    isMenuOpen = false,
    onClick
  } = props;

  const t = useAutoTranslation();
  const nameRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  // A clinic group's name is the site's own; the System Admin's "Global" is
  // ours (UserGroupChipCluster), so it is translated.
  const groupName = userGroup?.label || userGroup?.name || '';
  const name = userGroup?.global ? t(groupName) : groupName;
  const shortRole = USER_TYPE_SHORT_LABELS[userGroup?.userType] || '';
  const showRoleTag =
    shortRole && shortRole.length > 0 && shortRole.length <= ROLE_SHORT_MAX;

  useEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [name]);

  const ariaLabel = isMultiGroup
    ? t('Switch User Group. Currently in {{name}} as {{role}}', {
        name,
        role: roleFullName || ''
      }).trim()
    : t('Currently in {{name}} as {{role}}', {
        name,
        role: roleFullName || ''
      }).trim();

  const chipSx = {
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    pl: '12px',
    pr: '18px',
    borderRadius: '14px 0 0 14px',
    bgcolor: isMenuOpen
      ? 'rgba(255, 255, 255, 0.18)'
      : 'rgba(255, 255, 255, 0.10)',
    border: 'none',
    cursor: isMultiGroup ? 'pointer' : 'default',
    transition: 'background-color 120ms ease',
    color: '#fff',
    fontFamily: 'inherit',
    mr: '-10px',
    '&:hover': isMultiGroup
      ? { bgcolor: 'rgba(255, 255, 255, 0.16)' }
      : undefined,
    '&:focus-visible': {
      outline: '2px solid #fff',
      outlineOffset: 2
    }
  };

  const content = (
    <>
      <UserGroupMark userGroup={userGroup} size="sm" />
      <Box
        ref={nameRef}
        component="span"
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: '#fff',
          maxWidth: NAME_MAX_WIDTH,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1
        }}
      >
        {name}
      </Box>
      {showRoleTag && (
        <Box
          component="span"
          sx={{
            borderLeft: '1px solid rgba(255,255,255,0.15)',
            pl: '4px',
            fontSize: 10,
            color: '#8B96A8',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            lineHeight: 1,
            whiteSpace: 'nowrap'
          }}
        >
          {t(shortRole)}
        </Box>
      )}
      {isMultiGroup &&
        (isMenuOpen ? (
          <KeyboardArrowUpIcon
            aria-hidden="true"
            sx={{ fontSize: 14, color: '#9AA4B6' }}
          />
        ) : (
          <KeyboardArrowDownIcon
            aria-hidden="true"
            sx={{ fontSize: 14, color: '#9AA4B6' }}
          />
        ))}
    </>
  );

  const chip = isMultiGroup ? (
    <Box
      ref={ref}
      component="button"
      type="button"
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
      aria-label={ariaLabel}
      onClick={onClick}
      sx={chipSx}
    >
      {content}
    </Box>
  ) : (
    <Box ref={ref} role="img" aria-label={ariaLabel} sx={chipSx}>
      {content}
    </Box>
  );

  // Tooltip only when the name is actually truncated — avoids redundant
  // hover noise when the full name already fits.
  if (isTruncated) {
    return (
      <Tooltip title={name} arrow>
        {chip}
      </Tooltip>
    );
  }
  return chip;
});

export default UserGroupChip;
